import { Collider } from '../components/Collider';
import { SpriteRenderer } from '../components/SpriteRenderer';
import { type Scene } from '../scene/Scene';

const DEFAULT_WINDOW_FRAMES = 30;
const MILLISECONDS_PER_SECOND = 1000;

/** What the last stretch of frames looked like, and what the scene was holding during them. */
export interface DebugStats {
  /** Frames per second over the sample window. */
  readonly framesPerSecond: number;
  /** Mean frame time in milliseconds over the sample window. */
  readonly frameMilliseconds: number;
  /** Live entities in the scene. */
  readonly entityCount: number;
  /** Entities carrying a `SpriteRenderer`. */
  readonly spriteCount: number;
  /** Entities carrying a `Collider`. */
  readonly colliderCount: number;
}

/**
 * A rolling average of frame time, plus the scene counts worth watching beside it.
 *
 * Averaged rather than instantaneous because a single frame's delta jitters far too much to read: a
 * number that changes sixty times a second tells a human nothing. The window is short enough that a
 * stutter still shows up within half a second of causing it.
 */
export class FrameStatistics implements DebugStats {
  readonly #deltas: number[];

  #sampleCount = 0;
  #nextIndex = 0;
  #totalSeconds = 0;
  #entityCount = 0;
  #spriteCount = 0;
  #colliderCount = 0;

  constructor(windowFrames: number = DEFAULT_WINDOW_FRAMES) {
    this.#deltas = new Array<number>(Math.max(1, windowFrames)).fill(0);
  }

  /** Frames per second over the sample window; zero before the first frame. */
  get framesPerSecond(): number {
    const average = this.#averageSeconds;
    return average > 0 ? 1 / average : 0;
  }

  /** Mean frame time in milliseconds over the sample window. */
  get frameMilliseconds(): number {
    return this.#averageSeconds * MILLISECONDS_PER_SECOND;
  }

  /** Live entities in the scene as of the last sample. */
  get entityCount(): number {
    return this.#entityCount;
  }

  /** Entities carrying a `SpriteRenderer` as of the last sample. */
  get spriteCount(): number {
    return this.#spriteCount;
  }

  /** Entities carrying a `Collider` as of the last sample. */
  get colliderCount(): number {
    return this.#colliderCount;
  }

  /**
   * Folds one frame into the averages.
   *
   * @internal Called by `DebugSystem` once per frame; readers use the getters instead.
   */
  sample(scene: Scene, deltaSeconds: number): void {
    const replaced = this.#deltas[this.#nextIndex] ?? 0;
    this.#deltas[this.#nextIndex] = deltaSeconds;
    this.#totalSeconds += deltaSeconds - replaced;
    this.#nextIndex = (this.#nextIndex + 1) % this.#deltas.length;
    this.#sampleCount = Math.min(this.#sampleCount + 1, this.#deltas.length);

    this.#entityCount = scene.entityCount;
    this.#spriteCount = scene.getEntitiesWith(SpriteRenderer).length;
    this.#colliderCount = scene.getEntitiesWith(Collider).length;
  }

  /** Forgets every sample, so a restarted engine does not average across the gap. */
  reset(): void {
    this.#deltas.fill(0);
    this.#sampleCount = 0;
    this.#nextIndex = 0;
    this.#totalSeconds = 0;
    this.#entityCount = 0;
    this.#spriteCount = 0;
    this.#colliderCount = 0;
  }

  get #averageSeconds(): number {
    return this.#sampleCount === 0 ? 0 : this.#totalSeconds / this.#sampleCount;
  }
}
